import Api from "../../../../core/data/redux/utils/apiCall";
import { API_URLS } from "../../../../core/data/redux/apis/urls";

export const FORM_STORAGE_KEY = "leaveEncashmentFormDraft";

export const STATUS_FILTERS = [
  { id: "", labelKey: "all", label: "All" },
  { id: "draft", labelKey: "draft", label: "Draft" },
  { id: "pending", labelKey: "pending", label: "Pending" },
  { id: "approved", labelKey: "approved", label: "Approved" },
  { id: "rejected", labelKey: "rejected", label: "Rejected" },
  { id: "completed", labelKey: "completed", label: "Completed" },
];

const REQUEST_STATUS_LABELS: Record<string, { labelKey: string; label: string; badge: string }> = {
  N: { labelKey: "new", label: "New", badge: "badge bg-primary" },
  T: { labelKey: "to_be_approved", label: "To Be Approved", badge: "badge bg-warning text-dark" },
  P: { labelKey: "partially_approved", label: "Partially Approved", badge: "badge bg-warning text-dark" },
  A: { labelKey: "approved", label: "Approved", badge: "badge bg-success" },
  R: { labelKey: "rejected", label: "Rejected", badge: "badge bg-danger" },
  D: { labelKey: "processing_complete", label: "Processing Complete", badge: "badge bg-info" },
  B: { labelKey: "to_be_processed", label: "To Be Processed", badge: "badge bg-info" },
  I: { labelKey: "completed", label: "Completed", badge: "badge bg-success" },
  X: { labelKey: "deleted", label: "Deleted", badge: "badge bg-secondary" },
  K: { labelKey: "cancelled", label: "Cancelled", badge: "badge bg-danger" },
  DRAFT: { labelKey: "draft", label: "Draft", badge: "badge bg-secondary" },
  PENDING: { labelKey: "pending", label: "Pending", badge: "badge bg-warning text-dark" },
  APPROVED: { labelKey: "approved", label: "Approved", badge: "badge bg-success" },
  REJECTED: { labelKey: "rejected", label: "Rejected", badge: "badge bg-danger" },
  COMPLETED: { labelKey: "completed", label: "Completed", badge: "badge bg-success" },
};

export interface LeaveTypeOption {
  EmpID: string;
  LeaveTypeCode: string;
  LeaveTypeText: string;
  LeaveEncaMeth: string;
  LeaveEncaMethText: string;
}

export interface EncashmentMethodOption {
  EmpID: string;
  LeaveTypeCode: string;
  EncashmentMethod: string;
  EncashmentMethodText: string;
}

export interface LeaveEncashmentFormData {
  leaveType: string;
  method: string;
  days: string;
  note: string;
  ReferenceNumber: string;
  LeaveTypeCode: string;
  LeaveEncaMeth: string;
  LeaveTypeText: string;
  LeaveEncaMethText: string;
}

export interface RemarkRow {
  role: string;
  personnelNumber: string;
  name: string;
  remark: string;
}

export function getLoggedInUser(): any {
  try {
    const raw = localStorage.getItem("user");
    if (!raw || raw === "undefined") return {};
    const parsed = JSON.parse(raw);
    return parsed?.data?.user || parsed?.user || parsed || {};
  } catch {
    return {};
  }
}

export function getLoggedInEmpId(): string {
  const user = getLoggedInUser();
  return String(user?.details?.empId || user?.empId || user?.usrid || user?.emp_id || "").trim();
}

export function padEmpId(empId = getLoggedInEmpId()): string {
  const raw = String(empId || "").trim();
  if (!raw) return "";
  return raw.padStart(8, "0");
}

export function getEmployeeDisplayName(user = getLoggedInUser()): string {
  const details = user?.details || {};
  return (
    [details?.firstname, details?.lsastname || details?.secondname || details?.lastname]
      .filter(Boolean)
      .join(" ")
      .trim() || ""
  );
}

export function todayYmd(): string {
  const today = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
}

export function parseSapDate(dateStr?: string): string {
  if (!dateStr) return "-";
  const match = String(dateStr).match(/\/Date\((-?\d+)\)\//);
  if (!match) {
    const iso = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) return `${iso[3]}-${iso[2]}-${iso[1]}`;
    return String(dateStr);
  }
  const d = new Date(parseInt(match[1], 10));
  if (Number.isNaN(d.getTime())) return String(dateStr);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`;
}

export function formatIsoToDmy(value: any): string {
  if (!value) return "-";
  return parseSapDate(String(value));
}

function collectLayers(res: any): any[] {
  return [res, res?.data, res?.data?.data, res?.data?.data?.data].filter(
    (layer) => layer !== undefined && layer !== null
  );
}

export function extractList(res: any): any[] {
  for (const layer of collectLayers(res)) {
    if (Array.isArray(layer)) return layer;
    if (Array.isArray(layer?.data)) return layer.data;
    if (Array.isArray(layer?.d?.results)) return layer.d.results;
    if (Array.isArray(layer?.results)) return layer.results;
  }
  return [];
}

export function extractDetail(res: any): any | null {
  for (const layer of collectLayers(res)) {
    if (!layer || typeof layer !== "object" || Array.isArray(layer)) continue;
    if (layer.success === true && layer.data && typeof layer.data === "object" && !Array.isArray(layer.data)) {
      return layer.data;
    }
    if (layer.ReferenceNumber || layer.EmpId || layer.QuotaText || layer.QuotaType) {
      return layer;
    }
  }
  return null;
}

export function readApiResult(res: any): { ok: boolean; message: string } {
  for (const layer of collectLayers(res)) {
    if (layer && typeof layer === "object" && !Array.isArray(layer) && typeof layer.success === "boolean") {
      return { ok: layer.success === true, message: String(layer.message || "") };
    }
  }
  return { ok: false, message: "" };
}

export function getRequestStatusUi(raw: any, t: (key: string) => string): { label: string; badge: string } {
  const code = String(raw ?? "").trim().toUpperCase().replace(/[\s-]+/g, "_");
  const found = REQUEST_STATUS_LABELS[code];
  if (!found) {
    const text = String(raw ?? "").trim();
    return { label: text || "-", badge: "badge bg-secondary" };
  }
  const translated = t(found.labelKey);
  return { label: translated === found.labelKey ? found.label : translated, badge: found.badge };
}

export function canChangeRequest(item: any): boolean {
  const requestStatus = String(item?.RequestStatus ?? "").toUpperCase();
  return requestStatus === "N" || requestStatus === "T";
}

export function itemId(item: any): string {
  return String(item?.ReferenceNumber ?? item?.id ?? item?.Id ?? "");
}

export function displayEmpId(source: any, fallback = ""): string {
  const candidates = [
    source?.EmpId,
    source?.EmpID,
    source?.empId,
    source?.empID,
    source?.Pernr,
    source?.PERNR,
    fallback,
  ];
  for (const value of candidates) {
    const text = String(value ?? "").trim();
    if (text && text !== "undefined" && text !== "null" && text !== "-") return text;
  }
  return "";
}

export function buildRemarksRows(
  approverDetails: any[],
  t: (key: string) => string,
  employeeOverride?: { personnelNumber: string; name: string }
): RemarkRow[] {
  const list = Array.isArray(approverDetails) ? approverDetails : [];
  const rows: RemarkRow[] = [];
  const employee = employeeOverride ?? {
    personnelNumber: String(list[0]?.EmpID ?? list[0]?.EmpId ?? ""),
    name: String(list[0]?.EmployName ?? list[0]?.EmployeeName ?? ""),
  };
  if (employee.personnelNumber || employee.name) {
    rows.push({
      role: t("employee"),
      personnelNumber: employee.personnelNumber,
      name: employee.name,
      remark: "",
    });
  }
  list.forEach((approver: any) => {
    rows.push({
      role: t("approver"),
      personnelNumber: String(approver?.ApproverID ?? approver?.ApproverEmpId ?? ""),
      name: String(approver?.ApprverName ?? approver?.ApproverName ?? ""),
      remark: String(approver?.Comment ?? ""),
    });
  });
  return rows;
}

export function normalizeApproverList(res: any): any[] {
  const layers = collectLayers(res);
  for (const layer of layers) {
    if (Array.isArray(layer)) return layer;
    if (Array.isArray(layer?.data)) return layer.data;
  }
  const detail = extractDetail(res);
  return detail ? [detail] : [];
}

export function isLeaveEncashmentWorklistItem(task: {
  type?: string;
  taskText?: string;
  workItemText?: string;
}): boolean {
  const type = String(task?.type || "").toUpperCase().replace(/[\s-]+/g, "_");
  if (type === "LEAVE_ENCASH" || type === "LEAVE_ENCASHMENT" || type === "LEAVEENCASH") return true;
  const text = `${task?.taskText || ""} ${task?.workItemText || ""}`.toLowerCase();
  return text.includes("leave encash") || text.includes("leaveencash");
}

export async function fetchTimeQuota(empId = padEmpId()): Promise<any[]> {
  const res: any = await Api.post(API_URLS.LEAVE_ENCASHMENT.TIME_QUOTA, { EmpId: empId });
  // App reads Api.post body as res.data.data.
  const list = Array.isArray(res?.data?.data) ? res.data.data : extractList(res);
  const envelopeId = displayEmpId(res?.data, displayEmpId(res, empId));
  return list.map((item: any) => {
    const id = displayEmpId(item, envelopeId);
    return id ? { ...item, EmpId: id } : item;
  });
}

export async function fetchFindAll(fromDate?: string, toDate?: string): Promise<any[]> {
  const payload: Record<string, string> = {};
  if (fromDate) payload.fromDate = fromDate;
  if (toDate) payload.toDate = toDate;
  const res: any = await Api.post(API_URLS.LEAVE_ENCASHMENT.FIND_ALL, payload);
  // App apiCall: success when body.data.success / status 200, list is body.data.data.
  const inner = res?.data;
  if ((inner?.status === 200 || inner?.status === 201 || inner?.success === true) && Array.isArray(inner?.data)) {
    return inner.data;
  }
  if ((inner?.data?.status === 200 || inner?.data?.status === 201) && Array.isArray(inner?.data?.data)) {
    return inner.data.data;
  }
  if ((res?.status === 200 || res?.status === 201 || res?.success === true) && Array.isArray(res?.data)) {
    return res.data;
  }
  const failedMessage = String(
    (inner?.success === false && (inner?.message || inner?.data?.message)) ||
      (res?.success === false && res?.message) ||
      ""
  ).trim();
  if (failedMessage) {
    throw new Error(failedMessage);
  }
  return extractList(res);
}

export async function syncLeaveEncashment(empId = padEmpId()): Promise<{ ok: boolean; message: string }> {
  const res: any = await Api.post(API_URLS.LEAVE_ENCASHMENT.SYNC, { empId });
  return readApiResult(res);
}

export async function fetchLeaveTypes(empId = getLoggedInEmpId()): Promise<LeaveTypeOption[]> {
  const res: any = await Api.post(API_URLS.LEAVE_ENCASHMENT.LEAVE_TYPES, { EmpId: empId });
  return extractList(res);
}

export async function fetchEncashmentMethods(
  leaveTypeCode: string,
  empId = getLoggedInEmpId()
): Promise<EncashmentMethodOption[]> {
  const res: any = await Api.post(API_URLS.LEAVE_ENCASHMENT.METHODS, {
    EmpId: empId,
    LeaveTypeCode: leaveTypeCode,
  });
  return extractList(res);
}

export async function submitLeaveEncashment(
  formData: LeaveEncashmentFormData,
  isDraft: boolean
): Promise<{ ok: boolean; message: string }> {
  const isEditingDraft = !!formData.ReferenceNumber;
  const payload = {
    ReferenceNumber: formData.ReferenceNumber || "",
    Mode: isDraft ? "S" : isEditingDraft ? "U" : "N",
    ApproverEmpId: "00000000",
    ReimbursementType: "SLEA",
    RequestStatus: isDraft ? "N" : "T",
    StartDate: todayYmd(),
    QuotaType: formData.LeaveTypeCode || "",
    CompMethod: formData.LeaveEncaMeth || "",
    QuotaNumber: formData.days || "",
    QuotaText: formData.LeaveTypeText || formData.leaveType || "",
    Description: formData.LeaveEncaMethText || formData.method || "",
    Comment: formData.note || "",
  };
  const endpoint =
    !isDraft && isEditingDraft ? API_URLS.LEAVE_ENCASHMENT.UPDATE : API_URLS.LEAVE_ENCASHMENT.CREATE;
  const res: any = await Api.post(endpoint, payload);
  return readApiResult(res);
}

export async function deleteLeaveEncashment(item: any): Promise<{ ok: boolean; message: string }> {
  const payload = {
    ReferenceNumber: item?.ReferenceNumber ?? "",
    ReimbursementType: "SLEA",
    Mode: "D",
    RequestStatus: "T",
    QuotaType: item?.QuotaType ?? "",
    CompMethod: item?.CompMethod ?? "",
    QuotaNumber: item?.QuotaNumber ?? "",
    QuotaText: item?.QuotaText ?? "",
    Description: item?.Description ?? "",
    Comment: item?.Comment ?? "",
  };
  const res: any = await Api.post(API_URLS.LEAVE_ENCASHMENT.DELETE, payload);
  const result = readApiResult(res);
  if (res?.success === false || res?.data?.success === false) {
    return { ok: false, message: res?.data?.message || res?.message || result.message };
  }
  return { ok: result.ok || res?.success === true, message: result.message || res?.message || "" };
}

export async function findOneLeaveEncashment(referenceNumber: string): Promise<any | null> {
  const res: any = await Api.post(API_URLS.LEAVE_ENCASHMENT.FIND_ONE, { referenceNumber });
  return extractDetail(res);
}

export async function fetchApproverDetails(empId: string, referenceNumber: string): Promise<any[]> {
  const res: any = await Api.post(API_URLS.LEAVE_ENCASHMENT.APPROVER_DETAILS, {
    EmpId: empId,
    ReferenceNumber: referenceNumber,
  });
  return normalizeApproverList(res);
}

export async function approveRejectLeaveEncashment(payload: {
  ReferenceNumber: string;
  ApproverAction: "A" | "R";
  Comment: string;
}): Promise<{ ok: boolean; message: string }> {
  const res: any = await Api.post(API_URLS.LEAVE_ENCASHMENT.APPROVE_REJECT, payload);
  return readApiResult(res);
}

export function saveFormDraft(formData: LeaveEncashmentFormData) {
  sessionStorage.setItem(FORM_STORAGE_KEY, JSON.stringify(formData));
}

export function readFormDraft(): LeaveEncashmentFormData | null {
  try {
    const raw = sessionStorage.getItem(FORM_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearFormDraft() {
  sessionStorage.removeItem(FORM_STORAGE_KEY);
}

export function uniqueLeaveTypes(list: LeaveTypeOption[]): LeaveTypeOption[] {
  return list.filter(
    (item, index, self) =>
      index ===
      self.findIndex(
        (other) => other.LeaveTypeCode === item.LeaveTypeCode && other.LeaveEncaMeth === item.LeaveEncaMeth
      )
  );
}

export function leaveTypeLabel(item: LeaveTypeOption): string {
  return item.LeaveEncaMethText || item.LeaveTypeText || "";
}
